import { useCallback, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import moment from 'moment';

import { MasterNameApi } from '@/api';
import { DATE_FORMAT } from '@/config';
import { withLoader, WithLoaderProps } from '@/hocs';
import { INote } from '@/models/note';
import { MasterIndexDetailsContext } from '@/pages/MasterIndex/components/MasterIndexDetailsPage/MasterIndexDetailsPage';
import NotesDataGridWrapper, {
  getPlainTextFromDescription,
} from '@/pages/Notes/NotesDataGridWrapper';
import { ConfirmTypes, useToastStore } from '@/store';
import { formatPersonName } from '@/utils';

interface MasterIndexPersonNotesProps extends WithLoaderProps {
  viewId?: string;
  onCountChange?: () => void;
  readOnly?: boolean;
}

const MasterIndexPersonNotesComponent = ({
  showLoader,
  hideLoader,
  viewId,
  onCountChange,
  readOnly = false,
}: MasterIndexPersonNotesProps) => {
  const { updateToast } = useToastStore();
  const { id } = useParams();
  const personId = id || viewId;

  const { refreshTabCounts: refreshTabCountsContext } =
    useOutletContext<MasterIndexDetailsContext>() || {};
  const refreshTabCounts = onCountChange || refreshTabCountsContext;
  const [notes, setNotes] = useState<INote[]>([]);

  const [columnSettings, setColumnSettings] = useState<any[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const additionalColumns: GridColDef[] = useMemo<GridColDef[]>(
    () => [
      {
        field: 'description',
        headerName: 'Note description',
        flex: 1,
        renderCell: (params) => {
          const desc = getPlainTextFromDescription(params.row?.description);
          const display = desc.length > 25 ? `${desc.slice(0, 25)}...` : desc;
          return (
            <div className="flex h-full items-center">
              <Tooltip title={desc.length > 25 ? desc : ''} arrow>
                <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
                  {display}
                </Typography>
              </Tooltip>
            </div>
          );
        },
      },
      {
        field: 'createdBy',
        headerName: 'Created by',
        minWidth: 200,
        valueGetter: (_params, row) =>
          formatPersonName(row?.createdBy) || 'no name',
      },
      {
        field: 'createdAt',
        headerName: 'Created on',
        minWidth: 140,
        valueGetter: (_params, row) =>
          row?.createdAt ? moment(row.createdAt).format(DATE_FORMAT) : '',
      },
      {
        field: 'updatedBy',
        headerName: 'Last updated by',
        minWidth: 200,
        valueGetter: (_params, row) =>
          formatPersonName(row?.updatedBy) ||
          formatPersonName(row?.createdBy) ||
          '--',
      },
      {
        field: 'updatedAt',
        headerName: 'Last updated on',
        minWidth: 140,
        valueGetter: (_params, row) => {
          const ua = row?.updatedAt;
          const hasUpdater = Boolean(row?.updatedBy);
          return ua && hasUpdater
            ? moment(ua).format(DATE_FORMAT)
            : moment(row?.createdAt).format(DATE_FORMAT) || '--';
        },
      },

      {
        field: 'number',
        headerName: 'Referenced items',
        minWidth: 200,
        valueGetter: (_params, row) => {
          const cfs = row?.cfs?.number;
          const kase = row?.kase?.number;
          return cfs || kase || 'no number';
        },
      },
    ],
    [],
  );

  const fetchData = useCallback(
    async (
      page: number,
      limit: number,
      filterStr: string,
      sortStr?: string,
    ): Promise<{
      rows: INote[];
      totalCount?: number;
      hasNextPage?: boolean;
    }> => {
      if (!personId) return { rows: [], totalCount: 0, hasNextPage: false };

      showLoader();
      try {
        let sortObj: Record<string, 1 | -1> = { createdAt: -1 };
        if (sortStr) {
          try {
            const parsed = JSON.parse(sortStr);
            if (parsed && typeof parsed === 'object') {
              if ('field' in parsed && 'sort' in parsed) {
                sortObj = {
                  [parsed.field]: parsed.sort === 'asc' ? 1 : -1,
                } as Record<string, 1 | -1>;
              } else {
                sortObj = parsed as Record<string, 1 | -1>;
              }
            }
          } catch {
            // fallback to default
          }
        }

        const res = await MasterNameApi.getPaginatedMasterNameNotes(personId, {
          page,
          limit,
          filter: filterStr,
          sort: JSON.stringify(sortObj),
        });

        const rows: INote[] = Array.isArray(res?.data.docs)
          ? res.data.docs
          : [];

        setNotes(rows);

        return { rows, totalCount: res?.data?.totalDocs };
      } catch (err: any) {
        updateToast({
          open: true,
          message: err?.message ?? 'Failed to load notes',
        });
        return { rows: [], totalCount: 0, hasNextPage: false };
      } finally {
        hideLoader();
      }
    },
    [personId, showLoader, hideLoader, updateToast],
  );

  const onSaveNote = useCallback(
    async (payload: any) => {
      if (!personId) return;

      const noteText: string = payload?.comment ?? '';
      const noteId: string | undefined = payload?._id;

      showLoader();
      try {
        if (noteId) {
          await MasterNameApi.updateMasterNameNote(personId, noteId, noteText);
          updateToast({
            type: ConfirmTypes.SUCCESS,
            open: true,
            message: 'Note updated successfully',
          });
        } else {
          await MasterNameApi.addMasterNameNote(personId, noteText);
          updateToast({
            type: ConfirmTypes.SUCCESS,
            open: true,
            message: 'Note created successfully',
          });
        }
      } catch (err: any) {
        updateToast({
          open: true,
          message: err?.message ?? 'Failed to save note',
        });
      } finally {
        setRefreshKey((prevState) => prevState + 1);
        refreshTabCounts?.();
        hideLoader();
      }
    },
    [personId, showLoader, hideLoader, updateToast, refreshTabCounts],
  );

  const onDeleteNote = useCallback(
    async (noteId: string) => {
      if (!personId || !noteId) return;
      showLoader();
      try {
        await MasterNameApi.deleteMasterNameNote(personId, noteId);
        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: 'Note deleted successfully',
        });
      } catch (err: any) {
        updateToast({
          open: true,
          message: err?.message ?? 'Failed to delete note',
        });
      } finally {
        setRefreshKey((prevState) => prevState + 1);
        refreshTabCounts?.();
        hideLoader();
      }
    },
    [personId, showLoader, hideLoader, updateToast, refreshTabCounts],
  );

  const fetchNoteDetails = useCallback(
    async (noteId: string) => {
      try {
        return notes.find((note) => note._id === noteId);
      } catch (err: any) {
        updateToast({
          open: true,
          message: err?.message ?? 'Failed to load note details',
        });
        throw err;
      }
    },
    [updateToast, notes],
  );

  return (
    <NotesDataGridWrapper
      name="master-index-person-notes"
      fetchData={fetchData}
      columnSettings={columnSettings}
      setColumnSettings={setColumnSettings}
      readOnly={readOnly}
      additionalColumns={additionalColumns}
      fetchNoteDetails={fetchNoteDetails}
      onSaveNote={onSaveNote}
      onDeleteNote={onDeleteNote}
      hideQuickFilter={false}
      hideFilter={false}
      key={refreshKey}
    />
  );
};

export const MasterIndexPersonNotes = withLoader(
  MasterIndexPersonNotesComponent,
);
